import React from 'react';
import classNames from 'classnames';

import Store from 'store';
import Data from 'data';
import Locale from 'data/locale';

import { faChevronDown, faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { itemTypeTree, presetItems } from 'logic/itemTypes';
import Select from 'ui/select';
import { navigateToItem, NavigationMenu } from './Navigation';
import { D2ItemTooltip } from 'tooltips';
import EditingContext from 'store/editingContext';
import Accordion from 'ui/accordion';
import { VirtualListEx } from 'ui/virtual';
import Dialog from 'ui/dialog';
import { useContentCreator } from 'components/cclink';
import { itemNameColored } from './EquipmentPane';
import ItemFilterDialog, { itemFilterFunction, createItemFilter } from './ItemFilter';
import AdUnit from 'AdUnit';
import { itemCategories, renderBase } from './itemCategories';

const _L = Locale("ui-equipment");

function ItemTypeMenu({ filter, root, current, path, rootOffset, setPath }) {
  const [expanded, setExpanded] = React.useState(false);

  const menuRef = React.useRef();
  const onToggle = React.useCallback(() => {
    setExpanded(exp => !exp);
    menuRef.current.focus();
  }, [setExpanded]);
  const onBlur = React.useCallback(e => {
    if (!e.relatedTarget || !menuRef.current.contains(e.relatedTarget)) {
      setExpanded(false);
    }
  }, []);

  const types = React.useMemo(() => {
    if (!filter) return null;
    return Object.keys(Data.itemTypes).filter(filter);
  }, [filter]);

  const children = React.useMemo(() => {
    const children = root.children ? root.children.filter(item => !item.hidden) : [];
    children.unshift({
      name: root.name,
      filter: root.filter,
    });
    return children;
  }, [root]);
  const childrenFiltered = React.useMemo(() => (!types ? children :
    children.filter(item => !item.hidden && (item.separator || (!types || types.some(type => item.filter(type)))))
  ), [children, types]);

  const onNavigate = React.useCallback(path => {
    if (path[rootOffset] === 0) {
      setPath([...path.slice(0, rootOffset), "new"]);
    } else {
      path = [...path, "new"];
      path[rootOffset] -= 1;
      setPath(path);
    }
    setExpanded(false);
  }, [setPath, rootOffset]);

  return (
    <div className="d2p-item-type" tabIndex="-1" onBlur={onBlur} ref={menuRef}>
      <span className={classNames("d2p-type-nav", {"d2p-expanded": expanded})} onClick={onToggle}>
        {current.name}
        <FontAwesomeIcon icon={faChevronDown} className="ui-Select-arrow"/>
      </span>
      {!!expanded && <NavigationMenu items={childrenFiltered} fullItems={children} types={types} path={path.slice(0, rootOffset)} onNavigate={onNavigate} activePath={path.slice(rootOffset)} vertical/>}
    </div>
  )
}

function defaultOption(options) {
  while (options.length && options[0].options) {
    options = options[0].options;
  }
  return options[0];
}

const ItemCategory = ({ items, search, type, expanded, setExpanded, containerRect, onClick }) => {
  const isCC = useContentCreator();

  const flat = React.useMemo(() => {
    const result = [];
    function flatten(items, depth=0) {
      for (const item of items) {
        if (item.options) {
          result.push({title: item.title, depth});
          const count = result.length;
          flatten(item.options, depth + 1);
          if (result.length === count) result.pop();
        } else if (item.name.match(search) || item.baseName?.match(search)) {
          result.push({...item, depth});
        }
      }
    }
    flatten(items);
    return result;
  }, [items, search]);

  const onToggle = React.useCallback(() => !search && setExpanded(expanded => ({...expanded, [type]: !expanded[type]})), [type, setExpanded, search]);

  const render = itemCategories[type].render;
  const renderItem = React.useCallback(item => {
    if (item.title) {
      return <li key={item.title} style={{paddingLeft: item.depth * 10}}><h3>{item.title}</h3></li>;
    } else {
      return (
        <li key={item.id} className="d2p-item" style={{paddingLeft: item.depth * 10}} onClick={() => onClick(type, item.id)}>
          {render(item, search, isCC)}
        </li>
      );
    }
  }, [render, type, onClick, search, isCC]);

  if (!flat.length) return null;

  return (
    <li className={classNames("d2p-Section", {"d2p-expanded": expanded[type] || search, "d2p-always": !!search})}>
      <h3 onClick={() => onToggle(type)}>{itemCategories[type].title()}</h3>
      <Accordion visible={!!expanded[type] || search}>
        <VirtualListEx items={flat} renderItem={renderItem} containerRect={containerRect} className="d2p-item-list"/>
      </Accordion>
    </li>
  );
};

function CreateDialog({ onClose, onCreate, type, id, bases }) {
  const [base, setBase] = React.useState(() => bases && defaultOption(bases));
  const item = React.useMemo(() => itemCategories[type].create(id, base?.id, Store.instance.getProfile()), [type, id, base]);

  const onKeyDown = React.useCallback(e => {
    if (e.key === "Enter") {
      onCreate(item);
    }
  }, [onCreate, item]);

  return (
    <Dialog title={_L`Create item`} modal onClose={onClose} className="d2p-CreateItemDialog" onKeyDown={onKeyDown}>
      <Dialog.Content>
        <p>{_L`Create`} {(
          <D2ItemTooltip item={item}>
            <span className="d2p-item-link">{itemNameColored(item)}</span>
          </D2ItemTooltip>
        )}</p>
        {!!bases && (
          <div>
            {type !== 'normal' && _L`Base:` + ' '}<Select options={bases}
              value={base}
              onChange={setBase}
              renderValue={renderBase}
              renderOption={renderBase}
              searchLimit={10}
              searchMiddle={true}
              disabled={bases.reduce((sum, opt) => sum + (opt.options ? opt.options.length : 1), 0) <= 1}
            />
          </div>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onCreate(item)}>{_L`Create`}</button>
        <button onClick={onClose}>{_L`Cancel`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

const makeRegex = (str, middle) => {
  const prefix = (middle && !str.match(/^\s/) ? "" : "\\b") + "(";
  const suffix = ")" + (str.match(/\s$/) ? "\\b" : "");
  str = str.trim().replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
  if (!str) return;
  return new RegExp(prefix + str + suffix, "i");
};

export default React.memo(function ItemCreator({ path, filter, idFilter, setPath }) {
  const [expanded, setExpanded] = EditingContext.useState("itemCreateSection", {});
  const [creating, setCreating] = React.useState(null);
  const [containerRect, setContainerRect] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = EditingContext.useState("itemCreateFilters", []);
  const [showFilters, setShowFilters] = React.useState(false);

  let root = itemTypeTree;
  let rootOffset = 0;
  let current = itemTypeTree;
  path.forEach((index, offset) => {
    if (index === "new") return;
    current = current.children?.[index];
    if (current?.hidden) {
      root = current;
      rootOffset = offset + 1;
    }
  });
  if (!current) current = root;

  if (current.create) current = current.create;

  path = path.slice();
  if (path.length === 1) {
    path.splice(rootOffset, 0, 0);
  } else {
    path[rootOffset] += 1;
  }

  const store = Store.useStore();
  const dispatch = Store.useDispatch();
  const onCreate = React.useCallback((item, id) => {
    if (!item) return;
    if (!id) {
      const items = store.getState().items;
      id = 1;
      while (items[id]) id += 1;
      id = id.toString();
      dispatch.setItem(id, item);
    }

    setCreating(null);
    setPath(path => [...path.slice(0, rootOffset), ...navigateToItem(root, item), `item/${id}`]);
  }, [store, dispatch, setPath, root, rootOffset]);

  const makeFilterFunction = React.useCallback(type => {
    let filterFunc = itemFilterFunction(filters, type, store.getState());
    if (idFilter) {
      filterFunc = filterFunc || createItemFilter();
      filterFunc.funcs.base.push(idFilter);
    }
    return filterFunc;
  }, [filters, store, idFilter]);

  const items = React.useMemo(() => {
    const result = {};
    for (const type in itemCategories) {
      let filterFunc = makeFilterFunction(type);
      if (current === itemTypeTree) {
        filterFunc = filterFunc || createItemFilter();
        filterFunc.funcs.filter.push(id => !presetItems[id]);
      }
      result[type] = itemCategories[type].list(current, filter, filterFunc);
    }
    return result;
  }, [current, filter, makeFilterFunction]);

  const containerRef = React.useRef();
  const searchRef = React.useRef();
  React.useLayoutEffect(() => {
    setContainerRect(containerRef.current.getBoundingClientRect());
    searchRef.current.focus();
  }, []);
  const onScroll = React.useCallback(() => {
    setContainerRect(containerRef.current.getBoundingClientRect());
  }, []);

  const onItemClick = React.useCallback((type, id) => {
    const bases = itemCategories[type].bases?.(id, current, filter, makeFilterFunction(type));
    const state = store.getState();
    const profile = state.profiles[state.activeProfile];
    if (bases) {
      setCreating({type, id, bases});
    } else if (itemCategories[type].create) {
      onCreate(itemCategories[type].create(id, null, profile));
    } else if (presetItems[id]) {
      onCreate(presetItems[id], id);
    }
  }, [current, filter, onCreate, makeFilterFunction, store]);

  const onChangeSearch = React.useCallback(e => setSearch(e.target.value), []);
  const searchRe = React.useMemo(() => search.trim() && makeRegex(search, true), [search]);

  const onAcceptFilter = React.useCallback(result => {
    setFilters(result);
    setShowFilters(false);
  }, [setFilters]);

  const category = type => <ItemCategory items={items[type]} type={type} expanded={expanded}
    setExpanded={setExpanded} containerRect={containerRect} search={searchRe} onClick={onItemClick}/>;

  return (
    <div className="d2p-ItemCreator">
      <div className="d2p-header">
        <ItemTypeMenu filter={filter} root={root} current={current} path={path} rootOffset={rootOffset} setPath={setPath}/>
        <input className="d2p-search" type="text" placeholder={_L`Search`} value={search} onChange={onChangeSearch} ref={searchRef}/>
        <button className={classNames("d2p-filters", {"d2p-active": filters.length > 0})} onClick={() => setShowFilters(true)}>
          <FontAwesomeIcon icon={faFilter}/> {_L`Filter items`}
        </button>
      </div>
      <div className="d2p-category-list" ref={containerRef} onScroll={onScroll}>
        <div className="d2p-category-holder">
          <ul className="d2p-category-column">
            {category("runeword")}
            {category("crafted")}
            {category("socket")}
          </ul>
          <ul className="d2p-category-column">
            {category("unique")}
            {category("set")}
          </ul>
          <ul className="d2p-category-column">
            {category("normal")}
            {category("preset")}
          </ul>
        </div>
        <AdUnit id="d2planner-tab-equipment" className="d2p-TabFooter d2planner-tab-ad"/>
      </div>
      {!!creating && <CreateDialog onClose={() => setCreating(null)} onCreate={onCreate} type={creating.type} id={creating.id} bases={creating.bases}/>}
      {showFilters && <ItemFilterDialog onClose={() => setShowFilters(false)} onAccept={onAcceptFilter} initialValue={filters}/>}
    </div>
  );
});
